'use client';
import React, { createContext, useContext, useCallback, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import { Clock, Shield, Activity } from 'lucide-react';

interface AlertNotificationContextValue {
  notifyNewIncident: (title: string, severity?: 'critical' | 'high' | 'medium') => void;
  notifyMTTRBreach: (currentMTTR: number, thresholdMTTR: number, service?: string) => void;
  notifyComplianceViolation: (framework: string, control: string) => void;
  startSimulation: () => void;
  stopSimulation: () => void;
}

const AlertNotificationContext = createContext<AlertNotificationContextValue>({
  notifyNewIncident: () => {},
  notifyMTTRBreach: () => {},
  notifyComplianceViolation: () => {},
  startSimulation: () => {},
  stopSimulation: () => {},
});

const SEVERITY_COLORS = {
  critical: '#ef4444',
  high: '#f97316',
  medium: '#f59e0b',
};

const MOCK_INCIDENTS = [
  { title: 'Connection pool exhausted on db-prod-01', severity: 'critical' as const },
  { title: 'Memory pressure detected on api-gateway-02', severity: 'high' as const },
  { title: 'Slow query spike on analytics-db', severity: 'medium' as const },
  { title: 'Certificate expiry warning on auth-service', severity: 'high' as const },
  { title: 'Disk I/O saturation on storage-node-03', severity: 'medium' as const },
];

const MOCK_MTTR = [
  { current: 48, threshold: 30, service: 'payment-service' },
  { current: 62, threshold: 30, service: 'auth-gateway' },
  { current: 35, threshold: 30, service: 'api-proxy' },
];

const MOCK_COMPLIANCE = [
  { framework: 'SOC 2', control: 'CC6.1 — Logical Access Controls' },
  { framework: 'PCI DSS', control: 'Req 8.3 — MFA not enforced' },
  { framework: 'HIPAA', control: 'ePHI encryption at rest missing' },
];

export function AlertNotificationProvider({ children }: { children: React.ReactNode }) {
  const simulationRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const notifyNewIncident = useCallback(
    (title: string, severity: 'critical' | 'high' | 'medium' = 'high') => {
      const color = SEVERITY_COLORS[severity];
      toast.custom(
        (t) => (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              backgroundColor: 'var(--bg-card)',
              border: `1px solid ${color}40`,
              borderLeft: `3px solid ${color}`,
              borderRadius: '8px',
              padding: '12px 14px',
              minWidth: '320px',
              maxWidth: '400px',
              boxShadow: `0 4px 24px rgba(0,0,0,0.4), 0 0 0 1px ${color}20`,
            }}
          >
            <div
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '6px',
                backgroundColor: `${color}15`,
                border: `1px solid ${color}30`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                marginTop: '1px',
              }}
            >
              <Activity size={15} style={{ color }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                <span
                  style={{
                    fontFamily: 'JetBrains Mono, monospace',
                    fontSize: '9px',
                    fontWeight: 700,
                    color,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                  }}
                >
                  NEW INCIDENT · {severity.toUpperCase()}
                </span>
              </div>
              <div
                style={{
                  fontSize: '13px',
                  fontWeight: 500,
                  color: 'var(--text-primary)',
                  lineHeight: 1.4,
                  wordBreak: 'break-word',
                }}
              >
                {title}
              </div>
              <div
                style={{
                  fontFamily: 'JetBrains Mono, monospace',
                  fontSize: '10px',
                  color: 'var(--text-muted)',
                  marginTop: '4px',
                }}
              >
                {new Date().toLocaleTimeString()}
              </div>
            </div>
            <button
              onClick={() => toast.dismiss(t)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--text-muted)',
                fontSize: '16px',
                lineHeight: 1,
                padding: '0 2px',
                flexShrink: 0,
              }}
            >
              ×
            </button>
          </div>
        ),
        { duration: 6000 }
      );
    },
    []
  );

  const notifyMTTRBreach = useCallback((currentMTTR: number, thresholdMTTR: number, service = 'unknown-service') => {
    toast.custom(
      (t) => (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            backgroundColor: 'var(--bg-card)',
            border: '1px solid rgba(245, 158, 11, 0.4)',
            borderLeft: '3px solid #f59e0b',
            borderRadius: '8px',
            padding: '12px 14px',
            minWidth: '320px',
            maxWidth: '400px',
            boxShadow: '0 4px 24px rgba(0,0,0,0.4), 0 0 0 1px rgba(245,158,11,0.15)',
          }}
        >
          <div
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '6px',
              backgroundColor: 'rgba(245, 158, 11, 0.12)',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              marginTop: '1px',
            }}
          >
            <Clock size={15} style={{ color: '#f59e0b' }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ marginBottom: '3px' }}>
              <span
                style={{
                  fontFamily: 'JetBrains Mono, monospace',
                  fontSize: '9px',
                  fontWeight: 700,
                  color: '#f59e0b',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                }}
              >
                MTTR THRESHOLD BREACH
              </span>
            </div>
            <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)', lineHeight: 1.4 }}>
              {service}
            </div>
            <div
              style={{
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: '11px',
                color: '#f59e0b',
                marginTop: '4px',
              }}
            >
              {currentMTTR}min actual vs {thresholdMTTR}min target (+{currentMTTR - thresholdMTTR}min)
            </div>
          </div>
          <button
            onClick={() => toast.dismiss(t)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--text-muted)',
              fontSize: '16px',
              lineHeight: 1,
              padding: '0 2px',
              flexShrink: 0,
            }}
          >
            ×
          </button>
        </div>
      ),
      { duration: 7000 }
    );
  }, []);

  const notifyComplianceViolation = useCallback((framework: string, control: string) => {
    toast.custom(
      (t) => (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            backgroundColor: 'var(--bg-card)',
            border: '1px solid rgba(139, 92, 246, 0.4)',
            borderLeft: '3px solid #8b5cf6',
            borderRadius: '8px',
            padding: '12px 14px',
            minWidth: '320px',
            maxWidth: '400px',
            boxShadow: '0 4px 24px rgba(0,0,0,0.4), 0 0 0 1px rgba(139,92,246,0.15)',
          }}
        >
          <div
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '6px',
              backgroundColor: 'rgba(139, 92, 246, 0.12)',
              border: '1px solid rgba(139, 92, 246, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              marginTop: '1px',
            }}
          >
            <Shield size={15} style={{ color: '#8b5cf6' }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ marginBottom: '3px' }}>
              <span
                style={{
                  fontFamily: 'JetBrains Mono, monospace',
                  fontSize: '9px',
                  fontWeight: 700,
                  color: '#8b5cf6',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                }}
              >
                COMPLIANCE VIOLATION · {framework}
              </span>
            </div>
            <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)', lineHeight: 1.4 }}>
              {control}
            </div>
            <div
              style={{
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: '10px',
                color: 'var(--text-muted)',
                marginTop: '4px',
              }}
            >
              Detected at {new Date().toLocaleTimeString()} — Review required
            </div>
          </div>
          <button
            onClick={() => toast.dismiss(t)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--text-muted)',
              fontSize: '16px',
              lineHeight: 1,
              padding: '0 2px',
              flexShrink: 0,
            }}
          >
            ×
          </button>
        </div>
      ),
      { duration: 8000 }
    );
  }, []);

  const startSimulation = useCallback(() => {
    if (simulationRef.current) return;
    let step = 0;
    const events = [
      () => {
        const inc = MOCK_INCIDENTS[Math.floor(Math.random() * MOCK_INCIDENTS.length)];
        notifyNewIncident(inc.title, inc.severity);
      },
      () => {
        const m = MOCK_MTTR[Math.floor(Math.random() * MOCK_MTTR.length)];
        notifyMTTRBreach(m.current, m.threshold, m.service);
      },
      () => {
        const c = MOCK_COMPLIANCE[Math.floor(Math.random() * MOCK_COMPLIANCE.length)];
        notifyComplianceViolation(c.framework, c.control);
      },
    ];
    // Fire first immediately
    events[step % events.length]();
    step++;
    simulationRef.current = setInterval(() => {
      events[step % events.length]();
      step++;
    }, 8000);
  }, [notifyNewIncident, notifyMTTRBreach, notifyComplianceViolation]);

  const stopSimulation = useCallback(() => {
    if (simulationRef.current) {
      clearInterval(simulationRef.current);
      simulationRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      if (simulationRef.current) clearInterval(simulationRef.current);
    };
  }, []);

  return (
    <AlertNotificationContext.Provider
      value={{ notifyNewIncident, notifyMTTRBreach, notifyComplianceViolation, startSimulation, stopSimulation }}
    >
      {children}
    </AlertNotificationContext.Provider>
  );
}

export function useAlertNotifications() {
  return useContext(AlertNotificationContext);
}
